import React from "react";
import { Link } from "react-router-dom";
import { Award, Calendar, Crown, Heart, MapPin, Shield, Star, Users } from "lucide-react";
import { useProfileHub } from "../ProfileHubContext";
import ProfileSidebar from "./ProfileSidebar";
import ProfileMobileNav from "./ProfileMobileNav";

function formatTimeSince(value) {
  if (!value) return null;
  const diff = Math.floor((Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "aujourd'hui";
  if (diff === 1) return "il y a 1 jour";
  return `il y a ${diff} jours`;
}

export default function ProfileLayout({ children }) {
  const { errors, isLoading, profileIdentity, displayBadges } = useProfileHub();

  return (
    <div className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(95,181,111,0.18),transparent_28%),radial-gradient(circle_at_top_right,rgba(47,157,226,0.16),transparent_26%),linear-gradient(180deg,#fffdf5_0%,#effcf6_48%,#e9f7ff_100%)]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-[-10%] top-12 h-64 w-64 rounded-full bg-emerald-200/30 blur-3xl" />
        <div className="absolute right-[-8%] top-24 h-72 w-72 rounded-full bg-sky-200/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[1480px] px-4 py-8 sm:px-6 lg:px-8">
        <header
          className="mb-6 overflow-hidden rounded-[36px] border border-white/70 bg-white/85 bg-cover bg-center px-5 py-6 text-pixy-ink shadow-[0_24px_80px_rgba(47,157,226,0.16)] sm:px-7"
          style={{
            backgroundImage: profileIdentity.profile_background
              ? `linear-gradient(90deg,rgba(255,253,245,0.92),rgba(233,247,255,0.72)),url(${profileIdentity.profile_background})`
              : "linear-gradient(165deg,#fffdf5 0%,#effcf6 55%,#e9f7ff 100%)",
          }}
        >
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            {/* Identite joueur */}
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="relative shrink-0">
                {profileIdentity.avatar?.image ? (
                  <img
                    src={profileIdentity.avatar.image}
                    alt={profileIdentity.pseudo}
                    className="h-16 w-16 rounded-2xl border-2 border-white object-cover shadow-md sm:h-20 sm:w-20"
                  />
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-white bg-gradient-to-br from-pixy-green/30 to-pixy-blue/30 text-2xl shadow-md sm:h-20 sm:w-20">
                    {profileIdentity.pseudo?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                {profileIdentity.is_premium && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-400 shadow">
                    <Crown className="h-3 w-3 text-white" />
                  </span>
                )}
                <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-400 sm:h-4 sm:w-4" />
              </div>

              {/* Infos */}
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold tracking-tight text-pixy-ink sm:text-3xl">
                    {profileIdentity.pseudo}
                  </h1>
                  {profileIdentity.is_admin && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
                      <Star className="h-3 w-3" />
                      Admin
                    </span>
                  )}
                  {profileIdentity.is_moderator && !profileIdentity.is_admin && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
                      <Shield className="h-3 w-3" />
                      Modo
                    </span>
                  )}
                  {profileIdentity.is_premium && !profileIdentity.is_admin && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      <Crown className="h-3 w-3" />
                      Premium
                    </span>
                  )}
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  {profileIdentity.birth_year && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-pixy-blue" />
                      {new Date().getFullYear() - profileIdentity.birth_year} ans
                    </span>
                  )}
                  {profileIdentity.city && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5 text-pixy-green" />
                      {profileIdentity.city}
                    </span>
                  )}
                </div>
                {profileIdentity.bio && (
                  <p className="mt-1.5 max-w-sm text-xs text-slate-500 leading-relaxed">
                    {profileIdentity.bio}
                  </p>
                )}
              </div>
            </div>

            {/* Infos joueur */}
            <div className="flex items-end gap-4">
              {(profileIdentity.featured_badge || displayBadges.length > 0) && (() => {
                const badge = profileIdentity.featured_badge || displayBadges[0];
                const imgUrl = badge.icon_image || (badge.icon && badge.icon.startsWith("http") ? badge.icon : null);
                const emoji = !imgUrl && badge.icon ? badge.icon : null;
                return (
                  <div className="flex flex-col items-center gap-1">
                    {imgUrl ? (
                      <img src={imgUrl} alt={badge.name} className="h-16 w-16 object-contain drop-shadow-sm" />
                    ) : emoji ? (
                      <span className="text-5xl leading-none">{emoji}</span>
                    ) : (
                      <Award className="h-16 w-16 text-amber-400" />
                    )}
                    <span className="text-[10px] font-semibold text-amber-700 leading-none">{badge.name}</span>
                  </div>
                );
              })()}
              {profileIdentity.team && (
                <Link
                  to={`/equipe/t/${profileIdentity.team.id}`}
                  className="flex flex-col items-center gap-1 transition hover:opacity-80"
                >
                  {profileIdentity.team.logo ? (
                    <img src={profileIdentity.team.logo} alt={profileIdentity.team.name} className="h-8 w-8 rounded-xl object-cover border border-white shadow-sm" />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-sky-100 border border-white shadow-sm">
                      <Users className="h-4 w-4 text-sky-500" />
                    </div>
                  )}
                  <span className="text-[10px] font-bold text-sky-700">[{profileIdentity.team.tag}]</span>
                </Link>
              )}
              <div className="flex flex-col items-center gap-0.5 text-slate-500">
                <Heart className="h-5 w-5 text-rose-400" fill="currentColor" />
                <span className="text-xs font-medium text-slate-600">{profileIdentity.likes_count}</span>
              </div>
              {formatTimeSince(profileIdentity.last_login) && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/75 px-3 py-1.5 text-xs font-medium text-slate-500">
                  Dernière connexion {formatTimeSince(profileIdentity.last_login)}
                </span>
              )}
            </div>
          </div>
        </header>

        <ProfileMobileNav />

        <div className="mt-6 grid gap-6 xl:grid-cols-[280px,minmax(0,1fr)]">
          <ProfileSidebar />
          <div className="min-w-0">
            {isLoading ? (
              <div className="grid gap-4 sm:grid-cols-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-40 animate-pulse rounded-[28px] border border-white/20 bg-white/60"
                  />
                ))}
              </div>
            ) : (
              children
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
